<template>
  <v-layout>
    <!-- APP BAR -->
    <TheAppBar
      v-model:support-panel="supportPanel"
      :hide-logo="smAndDown"
      :is-mobile="device?.isMobile"
    >
      <template #desktop-prepend-end>
        <div
          v-if="
            (deepBoxBoxInfoStore.accessCompany &&
              !isBreakpointSmAndDownOrTabletDevice) ||
            isBoxPrivate
          "
          class="text-body-medium text-truncate font-weight-medium ml-2"
        >
          <!-- Show user display name if boxType `private`  -->
          <span v-if="isBoxPrivate && user && user.display_name">
            {{ user.display_name }}
          </span>
          <span v-else>
            {{ deepBoxBoxInfoStore.accessCompany?.displayName }}
          </span>
        </div>
        <BoxSelectionMenu v-else-if="!errorStore.error" />
      </template>

      <template
        v-if="
          !isBreakpointSmAndDownOrTabletDevice &&
          !isBoxPrivate &&
          !errorStore.error
        "
        #desktop-center
      >
        <BoxSelectionMenu />
      </template>

      <template v-if="!isBoxPrivate" #desktop-append>
        <DeepFlowTasksMenu
          v-if="settingsDevStore.canModeFeature('DEEP_FLOW')"
          :box-id="params.box"
          :organization-id="deepBoxBoxInfoStore.accessCompany?.companyId"
          :type-id="params.type"
        />
      </template>

      <!-- MOBILE -->
      <template v-if="globalStore.mobileSearch" #mobile-prepend>
        <v-btn
          class="mx-2"
          density="comfortable"
          icon="fas fa-arrow-left"
          @click="globalStore.mobileSearch = !globalStore.mobileSearch"
        ></v-btn>
      </template>
      <template #mobile-content>
        <div
          v-if="!globalStore.mobileSearch && !isBoxPrivate && !errorStore.error"
          class="d-flex ga-2"
        >
          <BoxSelectionMenu />
        </div>
        <SearchBarMobile v-else-if="globalStore.mobileSearch" />
        <div
          v-else-if="deepBoxBoxInfoStore.accessCompany || isBoxPrivate"
          class="text-body-medium text-truncate font-weight-medium"
        >
          <!-- Show user display name if boxType `private`  -->
          <span v-if="isBoxPrivate && user && user.display_name">
            {{ user.display_name }}
          </span>
          <span v-else>
            {{ deepBoxBoxInfoStore.accessCompany?.displayName }}
          </span>
        </div>
      </template>
      <template #mobile-append>
        <v-btn
          class="mx-2"
          density="comfortable"
          icon="far fa-magnifying-glass"
          @click="globalStore.mobileSearch = !globalStore.mobileSearch"
        ></v-btn>
      </template>
    </TheAppBar>
    <!-- /MOBILE -->
    <!-- /APP BAR -->

    <DeepSupportPanel v-model="supportPanel" />

    <!-- NAVIGATION MENU -->
    <BoxNavigationDrawer
      v-model="drawer"
      :box-id="deepBoxCoreStore.selectedBox?.boxNodeId"
      :type-id="deepBoxCoreStore.selectedBox?.deepBoxNodeId"
    />
    <!-- /NAVIGATION MENU -->

    <!-- DETAILS DRAWER -->
    <BoxDetailsDrawer
      :box-id="params.box"
      :folder-node-id="params.node"
      :loading="deepBoxDeepBoxesBoxesNodesStore.fetchNodesPending"
      :nodes="deepBoxDeepBoxesBoxesNodesStore.selectedNodes as Node[]"
      :organization-id="deepBoxBoxInfoStore.accessCompany?.companyId"
      :type-id="params.type"
      @update:comments:size="
        onUpdateNode($event.nodeId, { commentCount: $event.size })
      "
      @update:tags="onUpdateNode($event.nodeId, { tags: $event.tags })"
    />
    <!-- /DETAILS DRAWER -->

    <v-main fullscreen>
      <v-container class="pa-0 ma-0 fill-height deep-prevent-select" fluid>
        <ContainerContentOrError>
          <router-view />
        </ContainerContentOrError>
        <TasksAppWrapper>
          <TaskUploadsCard v-model:task-upload="taskUpload" />
          <NodeTasksZipDownloadCard />
        </TasksAppWrapper>
      </v-container>
    </v-main>
  </v-layout>
</template>

<script lang="ts" setup>
import { toast } from 'vue-sonner'
import { useDisplay } from 'vuetify'
import { useParams } from '@deepcloud/deep-ui-utils/router'
import { useDeepBoxBoxInfoStore } from '@/stores/deepbox/box-info.ts'
import { useDeepBoxCoreStore } from '@/stores/deepbox/core'
import { useDeepBoxDeepBoxesBoxesFavoritesStore } from '@/stores/deepbox/deepboxes/boxes/favorites.ts'
import { useDeepBoxDeepBoxesBoxesNodesStore } from '@/stores/deepbox/deepboxes/boxes/nodes'
import { useErrorStore } from '@/stores/error/error'
import { useGlobalStore } from '@/stores/global/global'
import { useSettingsDevStore } from '@/stores/settings/settings-dev'
import { DeviceKey } from '@/plugins/device-detector-js.ts'
import { useApi } from '@/composables/api.ts'
import { useKeycloak } from '@/composables/auth/keycloak'
import { useBox } from '@/composables/box/box'
import { createBoxBreadcrumbsContext } from '@/composables/box/box-breadcrumbs'
import { useBoxGeneric } from '@/composables/box/box-generic'
import { createContextBoxOverview } from '@/composables/context/ctx-box-overview.ts'
import { createContextBoxPrivate } from '@/composables/context/ctx-box-private'
import { createContextGenericFolders } from '@/composables/context/ctx-generics'
import TheAppBar from '@/components/appbar/TheAppBar.vue'
import BoxNavigationDrawer from '@/components/box/BoxNavigationDrawer.vue'
import BoxSelectionMenu from '@/components/box/BoxSelectionMenu.vue'
import BoxDetailsDrawer from '@/components/box/details-drawer/BoxDetailsDrawer.vue'
import ContainerContentOrError from '@/components/containers/ContainerContentOrError.vue'
import DeepFlowTasksMenu from '@/components/deepflow/DeepFlowTasksMenu.vue'
import DeepSupportPanel from '@/components/DeepSupportPanel.vue'
import NodeTasksZipDownloadCard from '@/components/node/NodeTasksZipDownloadCard.vue'
import SearchBarMobile from '@/components/search/SearchBarMobile.vue'
import TasksAppWrapper from '@/components/tasks/TasksAppWrapper.vue'
import { useDeepBoxUploadStore } from '@/components/upload-deepbox'
import TaskUploadsCard from '@/components/upload/components/TaskUploadsCard.vue'
import type {
  GenericFolder,
  Node,
  Overview,
  OverviewQueryParams,
} from '@deepcloud/api-deep-box'

const deepBoxUploadStore = useDeepBoxUploadStore()
const { taskUpload } = storeToRefs(deepBoxUploadStore)

const device = inject(DeviceKey)
const { smAndDown, lgAndUp } = useDisplay()
const { t, locale } = useI18n()
const { user } = useKeycloak()
const { deepBoxAPI } = useApi()

const supportPanel = ref(false)

const drawer = ref(lgAndUp.value)
provide('boxDrawer', drawer)

const deepBoxCoreStore = useDeepBoxCoreStore()
const deepBoxDeepBoxesBoxesNodesStore = useDeepBoxDeepBoxesBoxesNodesStore()
const errorStore = useErrorStore()
const globalStore = useGlobalStore()
const settingsDevStore = useSettingsDevStore()
const deepBoxBoxInfoStore = useDeepBoxBoxInfoStore()
const deepBoxDeepBoxesBoxesFavoritesStore =
  useDeepBoxDeepBoxesBoxesFavoritesStore()

const params = useParams<{
  type: string
  box: string
  node: string
}>()

const { isBoxPrivate } = useBox(computed(() => deepBoxCoreStore.selectedBox))

const { selectedBox } = storeToRefs(deepBoxCoreStore)

const segments = shallowRef(
  deepBoxDeepBoxesBoxesNodesStore.data?.path?.segments ?? [],
)

createBoxBreadcrumbsContext(segments, selectedBox)

const { onFetchGenericFolders } = useBoxGeneric()

const genericFolders = computedAsync<GenericFolder[]>(async () => {
  // using `locale` here to for update when `locale` is changed
  if (
    isBoxPrivate.value &&
    locale.value &&
    params.value.type &&
    params.value.box
  ) {
    const genericFolders = await onFetchGenericFolders(
      params.value.type,
      params.value.box,
    )

    return genericFolders || []
  }

  return []
}, [])

createContextGenericFolders(genericFolders)

watch(
  () => deepBoxDeepBoxesBoxesNodesStore.data,
  (newValue) => {
    if (newValue?.path?.segments) {
      segments.value = newValue.path.segments
    }
  },
  { immediate: true },
)

const isBreakpointSmAndDown = computed(() => smAndDown.value)

const isBreakpointSmAndDownOrTabletDevice = computed(
  () => device?.isTablet || isBreakpointSmAndDown.value,
)

const overview = ref<Overview>()

createContextBoxOverview(overview)
createContextBoxPrivate(computed(() => overview.value?.privateBox))

async function onFetchOverview(companyId: string) {
  try {
    const params: OverviewQueryParams = {
      'deepBoxes.boxes.limit': 0,
      companyId,
    }
    const res = await deepBoxAPI.value.overview.get(params)
    if (res.data) {
      overview.value = res.data
      if (res.data.favorites) {
        deepBoxDeepBoxesBoxesFavoritesStore.favorites =
          res.data.favorites?.boxes || []
      }
    }
  } catch {
    toast.error(t('error.error_occurred'))
  }
}

watch(
  () => deepBoxBoxInfoStore.accessCompany?.companyId,
  (newValue) => {
    if (newValue) {
      onFetchOverview(newValue)
    }
  },
  { immediate: true },
)

function onUpdateNode(changedNodeId: string, changes: Record<string, unknown>) {
  const node = deepBoxDeepBoxesBoxesNodesStore.data?.nodes?.find(
    (n) => n.nodeId === changedNodeId,
  )
  if (!node) return

  Object.assign(node, changes)
}
</script>
<style lang="scss" scoped>
.mobile_box_name {
  height: 20px;
  font-size: 13px;
  font-weight: bolder;
  margin: 4px 0 0;
  padding: 0;
}

.mobile_organisation_name {
  height: 20px;
  font-size: 10px;
  margin: -4px 0 0;
  padding: 0;
  color: rgb(var(--v-theme-grey));
}
</style>
